import {Validators} from '@angular/forms';
import {OnlyDigitsValidator, WhitespaceValidator} from '@spa/validators';
import {
    CurrencyTypesEnum,
    ProductFieldNames,
    FundProductFilterModel,
    CouponFrequency,
    FundProductStatusesEnum,
    FundProductTypesEnum,
    ProductInstrumentFieldNames,
    ProductRedemptionFieldNames,
    ProductCouponFieldNames,
    ProductSubType,
    ProductGroup,
    ProductCategory,
    SettlementType,
    BarrierType,
    WarrantType,
    FundProductInterface,
} from '@spa/api';

import {
    AmountControl,
    AutocompleteControl,
    BaseControl,
    CheckboxControl,
    DatepickerControl,
    Helper,
    RadioGroupControl,
    SelectControl
} from 'appShared/classes';

import {
    DialogButtonTypesEnum,
    DialogConfigInterface,
    ReportTableColumn,
    ReportTableConfigInterface,
    ReportTableData
} from 'appShared/components';

import {ColorCodesEnum, ComponentTypesEnum} from 'appShared/enums';
import {FilterControlInterface} from 'appShared/interfaces';
import {PanelSettingsProductsTrait} from 'appEntries/panel/traits';

export const SymbolMinLength = 3;
export const SymbolMaxLength = 12;
export const DurationInMonthsMaxLength = 12;
export const PanelSettingsProductsStorageId = 'PanelSettingsProductsComponent';
export const ProductEarlyRedemptionDateFields = [
    ProductFieldNames.SubscriptionPeriodEndsOn,
    ProductFieldNames.SubscriptionPeriodStartsOn,
    ProductFieldNames.InitialFixingDate,
    ProductFieldNames.FirstExchangeTradingDate,
    ProductFieldNames.IssuedOn,
    ProductFieldNames.FinalFixingDate,
    ProductFieldNames.LastExchangeTradingDate,
    ProductFieldNames.ExpiresOn,
    ProductFieldNames.Redemption,
] as string[];

export const ProductDateFields = [
    ProductFieldNames.SubscriptionPeriodEndsOn,
    ProductFieldNames.SubscriptionPeriodStartsOn,
    ProductFieldNames.InitialFixingDate,
    ProductFieldNames.FirstExchangeTradingDate,
    ProductFieldNames.IssuedOn,
    ProductFieldNames.FinalFixingDate,
    ProductFieldNames.LastExchangeTradingDate,
    ProductFieldNames.ExpiresOn,
    ProductFieldNames.Redemption,
] as string[];

export const MutualFundsDisabledFields = [
    ProductFieldNames.DurationInMonths,
    ProductFieldNames.SubscriptionPeriodEndsOn,
    ProductFieldNames.SubscriptionPeriodStartsOn,
    ProductFieldNames.InitialFixingDate,
    ProductFieldNames.FirstExchangeTradingDate,
    ProductFieldNames.FinalFixingDate,
    ProductFieldNames.LastExchangeTradingDate,
    ProductFieldNames.Redemption,
    ProductFieldNames.NominalValue,
    ProductFieldNames.AnnualInterestRate,
    ProductFieldNames.InterestPaymentPeriod,
    ProductFieldNames.Barrier,
    ProductFieldNames.BarrierType,
    ProductFieldNames.WorstPerformer,
    ProductFieldNames.StrikeLevel,
    ProductFieldNames.BonusLevel,
    ProductFieldNames.BonusAmount,
    ProductFieldNames.WarrantType,
    ProductFieldNames.MemoryEffect,
    ProductFieldNames.InitialLeverage,
    ProductFieldNames.WithdrawalBeforeMaturityFeeMin,
    ProductFieldNames.WithdrawalBeforeMaturityFeeMax,
    ProductFieldNames.MaximumBuyOrderSize,
    ProductFieldNames.Category,
    ProductFieldNames.SubType,
    ProductFieldNames.Group,
    ProductFieldNames.Guarantor,
    ProductFieldNames.CrossCurrency,
    ProductFieldNames.IssuePrice,
    ProductFieldNames.OpenEnd,
    ProductFieldNames.SettlementType,
    ProductFieldNames.ConditionalCoupon,
    ProductFieldNames.InterestPaymentPeriod,
    ProductFieldNames.Participation,
    ProductFieldNames.Short,
    ProductFieldNames.CapitalProtection,
    ProductFieldNames.CapLevel,
] as string[];

export const ProductInSubscriptionRequiredFields = [
    ProductFieldNames.SubscriptionPeriodEndsOn,
    ProductFieldNames.SubscriptionPeriodStartsOn,
] as string[];

export const NonCurrencyFields = [
    ProductFieldNames.InitialLeverage,
    ProductFieldNames.ConversionRatio,
    ProductFieldNames.BonusLevel,
] as string[];

export const ProductActiveRequiredFields = [
    ProductFieldNames.IssuedOn,
] as string[];

export const ProductCategoryAndGroupAndSubTypes: IProductCategoryAndGroupAndSubType[] = [
    {
        category: ProductCategory.YieldEnhancement,
        group: ProductGroup.BarrierDiscountCertificate,
        subType: ProductSubType.BarrierDiscountCertificate
    },
    {
        category: ProductCategory.YieldEnhancement,
        group: ProductGroup.BarrierDiscountCertificate,
        subType: ProductSubType.MultiBarrierDiscountCertificate
    },
    {
        category: ProductCategory.YieldEnhancement,
        group: ProductGroup.BarrierReverseConvertible,
        subType: ProductSubType.AutocallableMultiBarrierReverseConvertible
    },
    {
        category: ProductCategory.YieldEnhancement,
        group: ProductGroup.BarrierReverseConvertible,
        subType: ProductSubType.BarrierReverseConvertible
    },
    {
        category: ProductCategory.YieldEnhancement,
        group: ProductGroup.BarrierReverseConvertible,
        subType: ProductSubType.CallableMultiBarrierReverseConvertible
    },
    {
        category: ProductCategory.YieldEnhancement,
        group: ProductGroup.BarrierReverseConvertible,
        subType: ProductSubType.CallableBarrierReverseConvertible
    },
    {
        category: ProductCategory.YieldEnhancement,
        group: ProductGroup.BarrierReverseConvertible,
        subType: ProductSubType.LockInCertificate
    },
    {
        category: ProductCategory.YieldEnhancement,
        group: ProductGroup.BarrierReverseConvertible,
        subType: ProductSubType.MultiBarrierReverseConvertible
    },
    {
        category: ProductCategory.YieldEnhancement,
        group: ProductGroup.ConditionalCouponBarrierReverseConvertible,
        subType: ProductSubType.ConditionalCouponBarrierReverseConvertible
    },
    {
        category: ProductCategory.YieldEnhancement,
        group: ProductGroup.ConditionalCouponBarrierReverseConvertible,
        subType: ProductSubType.ConditionalCouponMultiBarrierReverseConvertible
    },
    {
        category: ProductCategory.YieldEnhancement,
        group: ProductGroup.ConditionalCouponReverseConvertible,
        subType: ProductSubType.ConditionalCouponMultiReverseConvertible
    },
    {
        category: ProductCategory.YieldEnhancement,
        group: ProductGroup.DiscountCertificate,
        subType: ProductSubType.DiscountCertificate
    },
    {
        category: ProductCategory.YieldEnhancement,
        group: ProductGroup.ExpressCertificates,
        subType: ProductSubType.MultiExpressCertificatewithBarrier
    },
    {
        category: ProductCategory.YieldEnhancement,
        group: ProductGroup.MiscellaneousYieldEnhancementCertificates,
        subType: ProductSubType.MiscellaneousYieldEnhancementCertificates
    },
    {
        category: ProductCategory.YieldEnhancement,
        group: ProductGroup.ReverseConvertible,
        subType: ProductSubType.AutocallableMultiReverseConvertible
    },
    {
        category: ProductCategory.YieldEnhancement,
        group: ProductGroup.ReverseConvertible,
        subType: ProductSubType.AutocallableReverseConvertible
    },
    {
        category: ProductCategory.YieldEnhancement,
        group: ProductGroup.ReverseConvertible,
        subType: ProductSubType.CallableMultiReverseConvertible
    },
    {
        category: ProductCategory.YieldEnhancement,
        group: ProductGroup.ReverseConvertible,
        subType: ProductSubType.CallableReverseConvertible
    },
    {
        category: ProductCategory.YieldEnhancement,
        group: ProductGroup.ReverseConvertible,
        subType: ProductSubType.MultiReverseConvertible
    },
    {
        category: ProductCategory.YieldEnhancement,
        group: ProductGroup.ReverseConvertible,
        subType: ProductSubType.ReverseConvertible
    },
    {
        category: ProductCategory.Participation,
        group: ProductGroup.BonusCertificate,
        subType: ProductSubType.BonusCertificate
    },
    {
        category: ProductCategory.Participation,
        group: ProductGroup.BonusCertificate,
        subType: ProductSubType.CappedBonusCertificate
    },
    {
        category: ProductCategory.Participation,
        group: ProductGroup.BonusCertificate,
        subType: ProductSubType.CappedMultiBonusCertificate
    },
    {
        category: ProductCategory.Participation,
        group: ProductGroup.BonusCertificate,
        subType: ProductSubType.CouponMultiBonusCertificate
    },
    {
        category: ProductCategory.Participation,
        group: ProductGroup.BonusCertificate,
        subType: ProductSubType.MultiBonusCertificate
    },
    {
        category: ProductCategory.Participation,
        group: ProductGroup.BonusOutperformanceCertificate,
        subType: ProductSubType.MultiBonusOutperformanceCertificate
    },
    {
        category: ProductCategory.Participation,
        group: ProductGroup.MiscellaneousParticipationCertificate,
        subType: ProductSubType.MiscellaneousParticipationCertificate
    },
    {
        category: ProductCategory.Participation,
        group: ProductGroup.OutperformanceCertificates,
        subType: ProductSubType.CappedOutperformanceCertificate
    },
    {
        category: ProductCategory.Participation,
        group: ProductGroup.OutperformanceCertificates,
        subType: ProductSubType.OutperformanceCertificate
    },
    {
        category: ProductCategory.Participation,
        group: ProductGroup.TrackerCertificate,
        subType: ProductSubType.ActivelyManagedCertificate
    },
    {
        category: ProductCategory.Participation,
        group: ProductGroup.TrackerCertificate,
        subType: ProductSubType.TrackerCertificate
    },
    {category: ProductCategory.Leverage, group: ProductGroup.MiniFuture, subType: ProductSubType.MiniFuture},
    {category: ProductCategory.Leverage, group: ProductGroup.Warrant, subType: ProductSubType.Warrant},
    {
        category: ProductCategory.InvestmentProductswithadditionalcreditrisk,
        group: ProductGroup.ReferenceEntityCertificatewithConditionalCapitalProtection,
        subType: ProductSubType.ReferenceEntityCertificatewithConditionalCapitalProtection
    },
    {
        category: ProductCategory.CapitalProtection,
        group: ProductGroup.CapitalProtectionCertificatewithBarrier,
        subType: ProductSubType.CapitalProtectionCertificatewithBarrier
    },
    {
        category: ProductCategory.CapitalProtection,
        group: ProductGroup.CapitalProtectionCertificatewithCoupon,
        subType: ProductSubType.AutocallablesCapitalProtectionCertificatewithCoupon
    },
    {
        category: ProductCategory.CapitalProtection,
        group: ProductGroup.CapitalProtectionCertificatewithCoupon,
        subType: ProductSubType.CapitalProtectionCertificatewithCoupon
    },
    {
        category: ProductCategory.CapitalProtection,
        group: ProductGroup.CapitalProtectionCertificatewithParticipation,
        subType: ProductSubType.CapitalProtectedCertificatewithCap
    },
    {
        category: ProductCategory.CapitalProtection,
        group: ProductGroup.CapitalProtectionCertificatewithParticipation,
        subType: ProductSubType.CapitalProtectedCertificatewithoutCap
    },
    {
        category: ProductCategory.CapitalProtection,
        group: ProductGroup.MiscellaneousCapitalProtectedCertificates,
        subType: ProductSubType.MiscellaneousCapitalProtectedCertificates
    },
];

export interface IProductCategoryAndGroupAndSubType {
    category: ProductCategory;
    group: ProductGroup;
    subType: ProductSubType;
}

export interface IUpdateProductStatusControls {
    status: RadioGroupControl;
    buyOrdersAllowed: CheckboxControl;
    sellOrdersAllowed: CheckboxControl;
}

export interface ICreateProductControls {
    type: SelectControl;
    name: BaseControl;
    nameBG: BaseControl;
    code: BaseControl;
    version: BaseControl;
    currency: SelectControl;
    issuedBy: BaseControl;
    issuedOn: DatepickerControl;
    expiresOn: DatepickerControl;
    durationInMonths: BaseControl;
    nominalValue: BaseControl;
}

export interface IUpdateProductControls extends ICreateProductControls {
    subType: SelectControl;
    group: SelectControl;
    category: SelectControl;
    isin: BaseControl;
    guarantor: BaseControl;
    listingMarket: BaseControl;
    exchangeSymbol: BaseControl;

    crossCurrency: CheckboxControl;
    issuePrice: BaseControl;
    openEnd: CheckboxControl;
    settlementType: SelectControl;

    subscriptionPeriodStartsOn: DatepickerControl;
    subscriptionPeriodEndsOn: DatepickerControl;
    initialFixingDate: DatepickerControl;
    firstExchangeTradingDate: DatepickerControl;
    finalFixingDate: DatepickerControl;
    lastExchangeTradingDate: DatepickerControl;
    redemption: DatepickerControl;

    memoryEffect: CheckboxControl;
    conditionalCoupon: CheckboxControl;
    annualInterestRate: AmountControl;
    interestPaymentPeriod: SelectControl;
    couponTriggerLevel: AmountControl;

    participation: AmountControl;
    capLevel: AmountControl;
    short: CheckboxControl;
    capitalProtection: AmountControl;

    barrier: AmountControl;
    barrierType: SelectControl;
    worstPerformer: CheckboxControl;

    strikeLevel: AmountControl;
    bonusLevel: AmountControl;
    bonusAmount: AmountControl;

    warrantType: SelectControl;
    conversionRatio: AmountControl;
    initialLeverage: AmountControl;

    minimumBuyOrderSize?: AmountControl;
    maximumBuyOrderSize?: AmountControl;

    withdrawalBeforeMaturityFeeMin: AmountControl;
    withdrawalBeforeMaturityFeeMax: AmountControl;
}

export interface IUpdateCouponPaymentControls {
    observationDate: DatepickerControl;
    paymentDate: DatepickerControl;
    amount: AmountControl;
}

export interface IUpdateEarlyRedemptionControls {
    amount: AmountControl;
    triggerLevel: AmountControl;
    observationDate: DatepickerControl;
    date: DatepickerControl;
}

export interface IUpdateUnderlyingInstrumentControls {
    name: BaseControl;
    symbol: AutocompleteControl;
    currency: BaseControl;
    price: BaseControl;
    weight: BaseControl;
    initialFixingLevel: BaseControl;
}

export const PanelSettingsProductsFilterControls = (): FilterControlInterface[] => {
    return [
        {
            name: FundProductFilterModel.nameSelector(),
            placeholder: 'Name',
            controls: [
                new BaseControl({
                    name: FundProductFilterModel.nameSelector(),
                    value: null,
                    placeholder: 'Name',
                    validators: [],
                    componentType: ComponentTypesEnum.Text
                }),
            ],
            sortIndex: 1
        },
        {
            name: FundProductFilterModel.codeSelector(),
            placeholder: 'Symbol',
            controls: [
                new BaseControl({
                    name: FundProductFilterModel.codeSelector(),
                    value: null,
                    placeholder: 'Symbol',
                    validators: [],
                    componentType: ComponentTypesEnum.Text
                }),
            ],
            sortIndex: 2
        },
        {
            name: FundProductFilterModel.typeSelector(),
            placeholder: 'Type',
            controls: [
                new SelectControl({
                    name: FundProductFilterModel.typeSelector(),
                    value: null,
                    placeholder: 'Type',
                    validators: [],
                    componentType: ComponentTypesEnum.Select,
                    options: [
                        {
                            label: Helper.textsHelper().getProductTypeName(FundProductTypesEnum.All),
                            value: null
                        },
                        {
                            label: Helper.textsHelper().getProductTypeName(FundProductTypesEnum.MutualFund),
                            value: FundProductTypesEnum.MutualFund
                        },
                        {
                            label: Helper.textsHelper().getProductTypeName(FundProductTypesEnum.InvestmentCertificate),
                            value: FundProductTypesEnum.InvestmentCertificate
                        },
                        {
                            label: Helper.textsHelper().getProductTypeName(FundProductTypesEnum.Bonds),
                            value: FundProductTypesEnum.Bonds
                        },
                    ]
                })
            ],
            sortIndex: 3
        },
        {
            name: FundProductFilterModel.statusSelector(),
            placeholder: 'Status',
            controls: [
                new SelectControl({
                    name: FundProductFilterModel.statusSelector(),
                    value: null,
                    placeholder: 'Status',
                    validators: [],
                    componentType: ComponentTypesEnum.Select,
                    options: [
                        {
                            label: 'All',
                            value: null
                        },
                        {
                            label: Helper.textsHelper().getProductStatusName(FundProductStatusesEnum.Inactive),
                            value: FundProductStatusesEnum.Inactive
                        },
                        {
                            label: Helper.textsHelper().getProductStatusName(FundProductStatusesEnum.InSubscription),
                            value: FundProductStatusesEnum.InSubscription
                        },
                        {
                            label: Helper.textsHelper().getProductStatusName(FundProductStatusesEnum.Active),
                            value: FundProductStatusesEnum.Active
                        },
                        {
                            label: Helper.textsHelper().getProductStatusName(FundProductStatusesEnum.Expired),
                            value: FundProductStatusesEnum.Expired
                        },
                    ]
                })
            ],
            sortIndex: 4
        },
    ];
};

export const PanelSettingsProductsDataTableItems = (): ReportTableData[] => {
    return [
        new ReportTableData<string>({
            colId: ProductFieldNames.ProductId,
            value: null,
            hidden: false
        }),
        new ReportTableData<string>({
            colId: ProductFieldNames.Name,
            value: null,
            hidden: false
        }),
        new ReportTableData<string>({
            colId: ProductFieldNames.Code,
            value: null,
            hidden: false
        }),
        new ReportTableData<string>({
            colId: ProductFieldNames.Type,
            value: null,
            hidden: false
        }),
        new ReportTableData<string>({
            colId: ProductFieldNames.IssuedOn,
            value: null,
            hidden: false
        }),
        new ReportTableData<string>({
            colId: ProductFieldNames.ExpiresOn,
            value: null,
            hidden: false
        }),
        new ReportTableData<string>({
            colId: ProductFieldNames.Status,
            value: null,
            hidden: false
        }),
        new ReportTableData<string>({
            colId: ProductFieldNames.CreatedBy,
            value: null,
            hidden: false
        }),
    ];
};

export const PanelSettingsProductsDataTableConfig: ReportTableConfigInterface = {
    rows: [],
    columns: [
        new ReportTableColumn({
            label: 'Product Id',
            selector: ProductFieldNames.ProductId,
        }),
        new ReportTableColumn({
            label: 'Name',
            selector: ProductFieldNames.Name
        }),
        new ReportTableColumn({
            label: 'Symbol',
            selector: ProductFieldNames.Code,
        }),
        new ReportTableColumn({
            label: 'Type',
            selector: ProductFieldNames.Type
        }),
        new ReportTableColumn({
            label: 'Issue Date',
            selector: ProductFieldNames.IssuedOn
        }),
        new ReportTableColumn({
            label: 'Maturity',
            selector: ProductFieldNames.ExpiresOn
        }),
        new ReportTableColumn({
            label: 'Status',
            selector: ProductFieldNames.Status
        }),
        new ReportTableColumn({
            label: 'Created By',
            selector: ProductFieldNames.CreatedBy
        })
    ],
    hiddenColumnIds: [],
    actionMenu: false
};

export const UpdateProductStatusControls = (): IUpdateProductStatusControls => {
    return {
        status: new RadioGroupControl({
            name: ProductFieldNames.Status,
            value: null,
            placeholder: 'Status',
            validators: [Validators.required],
            componentType: ComponentTypesEnum.RadioGroup,
            invalid: false,
            errors: [],
            options: [
                {
                    value: FundProductStatusesEnum.Inactive,
                    label: Helper.textsHelper().getProductStatusName(FundProductStatusesEnum.Inactive),
                    color: ColorCodesEnum.Primary
                },
                {
                    value: FundProductStatusesEnum.InSubscription,
                    label: Helper.textsHelper().getProductStatusName(FundProductStatusesEnum.InSubscription),
                    color: ColorCodesEnum.Primary,
                },
                {
                    value: FundProductStatusesEnum.Active,
                    label: Helper.textsHelper().getProductStatusName(FundProductStatusesEnum.Active),
                    color: ColorCodesEnum.Primary,
                },
                {
                    value: FundProductStatusesEnum.Expired,
                    label: Helper.textsHelper().getProductStatusName(FundProductStatusesEnum.Expired),
                    color: ColorCodesEnum.Primary
                },
            ]
        }),
        buyOrdersAllowed: new CheckboxControl({
            name: ProductFieldNames.BuyOrdersAllowed,
            value: '',
            placeholder: 'Buy Orders Allowd',
            validators: [],
            componentType: ComponentTypesEnum.Checkbox,
            checked: null,
            color: ColorCodesEnum.Primary,
            expectBooleanValue: true,
        }),
        sellOrdersAllowed: new CheckboxControl({
            name: ProductFieldNames.SellOrdersAllowed,
            value: '',
            placeholder: 'Sell Orders Allowd',
            validators: [],
            componentType: ComponentTypesEnum.Checkbox,
            checked: null,
            color: ColorCodesEnum.Primary,
            expectBooleanValue: true
        }),
    };
};

export const CreateProductFormControls = (): ICreateProductControls => {
    return {
        type: new SelectControl({
            name: ProductFieldNames.Type,
            value: null,
            placeholder: 'Type *',
            validators: [Validators.required],
            componentType: ComponentTypesEnum.Select,
            floatingLabelNever: false,
            options: [
                {
                    value: FundProductTypesEnum.MutualFund,
                    label: Helper.textsHelper().getProductTypeName(FundProductTypesEnum.MutualFund),
                },
                {
                    value: FundProductTypesEnum.InvestmentCertificate,
                    label: Helper.textsHelper().getProductTypeName(FundProductTypesEnum.InvestmentCertificate),
                },
                {
                    label: Helper.textsHelper().getProductTypeName(FundProductTypesEnum.Bonds),
                    value: FundProductTypesEnum.Bonds,
                },
            ]
        }),
        name: new BaseControl({
            name: ProductFieldNames.Name,
            value: null,
            placeholder: 'Name EN *',
            validators: [WhitespaceValidator()],
            componentType: ComponentTypesEnum.Text,
            floatingLabelNever: false,
        }),
        nameBG: new BaseControl({
            name: ProductFieldNames.NameBG,
            value: null,
            placeholder: 'Name BG',
            validators: [],
            componentType: ComponentTypesEnum.Text,
            floatingLabelNever: false,
        }),
        code: new BaseControl({
            name: ProductFieldNames.Code,
            value: null,
            placeholder: 'Symbol *',
            validators: [WhitespaceValidator(), Validators.minLength(SymbolMinLength), Validators.maxLength(SymbolMaxLength)],
            componentType: ComponentTypesEnum.Text,
            floatingLabelNever: false,
        }),
        version: new BaseControl({
            name: ProductFieldNames.Version,
            value: null,
            placeholder: 'Valor',
            validators: [WhitespaceValidator()],
            componentType: ComponentTypesEnum.Text,
            floatingLabelNever: true,
            nonCapitalizedPlaceholder: true
        }),
        currency: new SelectControl({
            name: ProductFieldNames.Currency,
            value: CurrencyTypesEnum.Euro,
            placeholder: 'Currency *',
            validators: [Validators.required],
            componentType: ComponentTypesEnum.Select,
            floatingLabelNever: false,
            options: Helper.getCurrencyOptions(),

        }),
        issuedBy: new BaseControl({
            name: ProductFieldNames.IssuedBy,
            value: null,
            placeholder: 'Issuer *',
            validators: [WhitespaceValidator()],
            componentType: ComponentTypesEnum.Text,
            floatingLabelNever: false,
        }),
        issuedOn: new DatepickerControl({
            name: ProductFieldNames.IssuedOn,
            value: null,
            placeholder: 'Issue Date',
            validators: [],
            componentType: ComponentTypesEnum.Datepicker,
            floatingLabelNever: false,
            resetable: true
        }),
        expiresOn: new DatepickerControl({
            name: ProductFieldNames.ExpiresOn,
            value: null,
            placeholder: 'Maturity',
            validators: [],
            componentType: ComponentTypesEnum.Datepicker,
            floatingLabelNever: false,
            resetable: true
        }),
        durationInMonths: new BaseControl({
            name: ProductFieldNames.DurationInMonths,
            value: null,
            placeholder: 'Duration in Months',
            validators: [OnlyDigitsValidator(), Validators.maxLength(DurationInMonthsMaxLength)],
            componentType: ComponentTypesEnum.Text,
            floatingLabelNever: false,
        }),
        nominalValue: new AmountControl({
            name: ProductFieldNames.NominalValue,
            value: null,
            placeholder: 'Denomination',
            validators: [],
            componentType: ComponentTypesEnum.AmountInput,
            floatingLabelNever: false,
            currency: ''
        }),
    };
};

export const UpdateProductFormControls = (product: FundProductInterface): IUpdateProductControls => {
    const isCertificateOrBond = PanelSettingsProductsTrait.isProductCertificateOrBond(product);
    const isCertificate = PanelSettingsProductsTrait.isProductCertificate(product);
    const isBonds = PanelSettingsProductsTrait.isProductBonds(product);
    const controls = CreateProductFormControls() as IUpdateProductControls;
    const positiveDecimalValidator = Validators.min(0.01);

    controls.subType = new SelectControl({
        name: ProductFieldNames.SubType,
        value: null,
        placeholder: 'Sub Type',
        validators: isCertificateOrBond ? [Validators.required] : [],
        componentType: ComponentTypesEnum.Select,
        options: []
    });

    const bondSubTypes = [ProductSubType.CorporateBonds, ProductSubType.MunicipalBonds, ProductSubType.GovernmentBonds];

    Helper.getEnumNumberValues(ProductSubType)
        .forEach(key => {
            if (isBonds && bondSubTypes.indexOf(key) >= 0 || isCertificate && bondSubTypes.indexOf(key) < 0) {
                controls.subType.options.push({
                    value: key,
                    label: Helper.textsHelper().getProductSubTypeName(key),
                });
            }
        });

    controls.group = new SelectControl({
        name: ProductFieldNames.Group,
        value: null,
        placeholder: 'Group',
        validators: isCertificate ? [Validators.required] : [],
        componentType: ComponentTypesEnum.Select,
        options: []
    });

    if (isCertificate) {
        Helper.getEnumNumberValues(ProductGroup)
            .forEach(key => controls.group.options.push({
                value: key,
                label: Helper.textsHelper().getProductGroupName(key)
            }));
    }

    controls.category = new SelectControl({
        name: ProductFieldNames.Category,
        value: null,
        placeholder: 'Category',
        validators: isCertificateOrBond ? [Validators.required] : [],
        componentType: ComponentTypesEnum.Select,
        options: []
    });

    const bondCategories = [ProductCategory.FixedRateBonds, ProductCategory.FloatingRateBonds, ProductCategory.ZeroCouponBonds];

    Helper.getEnumNumberValues(ProductCategory)
        .forEach(key => {
            if (isBonds && bondCategories.indexOf(key) >= 0 || isCertificate && bondCategories.indexOf(key) < 0) {
                controls.category.options.push({
                    value: key,
                    label: Helper.textsHelper().getProductCategoryName(key)
                })
            }
        });

    controls.isin = new BaseControl({
        name: ProductFieldNames.ISIN,
        value: null,
        placeholder: 'ISIN',
        validators: [],
        componentType: ComponentTypesEnum.Text,
        nonCapitalizedPlaceholder: true
    });

    controls.guarantor = new BaseControl({
        name: ProductFieldNames.Guarantor,
        value: null,
        placeholder: 'Guarantor',
        validators: [],
        componentType: ComponentTypesEnum.Text,
    });

    controls.listingMarket = new BaseControl({
        name: ProductFieldNames.ListingMarket,
        value: null,
        placeholder: 'Listing Market',
        validators: [],
        componentType: ComponentTypesEnum.Text,
    });

    controls.exchangeSymbol = new BaseControl({
        name: ProductFieldNames.ExchangeSymbol,
        value: null,
        placeholder: 'Exchange Symbol',
        validators: [],
        componentType: ComponentTypesEnum.Text,
    });

    controls.crossCurrency = new CheckboxControl({
        name: ProductFieldNames.CrossCurrency,
        value: '',
        placeholder: 'Cross Currency',
        validators: [],
        componentType: ComponentTypesEnum.Checkbox,
        checked: null,
        color: ColorCodesEnum.Primary,
        expectBooleanValue: true,
    });

    controls.issuePrice = new AmountControl({
        name: ProductFieldNames.IssuePrice,
        value: null,
        placeholder: 'Issue Price',
        validators: isCertificateOrBond ? [Validators.required, positiveDecimalValidator] : [],
        componentType: ComponentTypesEnum.AmountInput,
        currency: '',
        isPercentage: true
    });

    controls.openEnd = new CheckboxControl({
        name: ProductFieldNames.OpenEnd,
        value: '',
        placeholder: 'Open End',
        validators: [],
        componentType: ComponentTypesEnum.Checkbox,
        checked: null,
        color: ColorCodesEnum.Primary,
        expectBooleanValue: true,
    });

    controls.settlementType = new SelectControl({
        name: ProductFieldNames.SettlementType,
        value: null,
        placeholder: 'Settlement Type',
        validators: [],
        componentType: ComponentTypesEnum.Select,
        options: []
    });

    Helper.getEnumNumberValues(SettlementType)
        .forEach(key => controls.settlementType.options.push({
            value: key,
            label: Helper.textsHelper().getProductSettlementTypeName(key)
        }));

    controls.subscriptionPeriodStartsOn = new DatepickerControl({
        name: ProductFieldNames.SubscriptionPeriodStartsOn,
        value: null,
        placeholder: 'Subscription Period Starts',
        validators: [],
        componentType: ComponentTypesEnum.Datepicker,
        resetable: true
    });

    controls.subscriptionPeriodEndsOn = new DatepickerControl({
        name: ProductFieldNames.SubscriptionPeriodEndsOn,
        value: null,
        placeholder: 'Subscription Period Ends',
        validators: [],
        componentType: ComponentTypesEnum.Datepicker,
        resetable: true
    });

    controls.initialFixingDate = new DatepickerControl({
        name: ProductFieldNames.InitialFixingDate,
        value: null,
        placeholder: 'Initial Fixing Date',
        validators: [],
        componentType: ComponentTypesEnum.Datepicker,
        resetable: true
    });

    controls.firstExchangeTradingDate = new DatepickerControl({
        name: ProductFieldNames.FirstExchangeTradingDate,
        value: null,
        placeholder: 'First Exchange Trading Date',
        validators: [],
        componentType: ComponentTypesEnum.Datepicker,
        resetable: true
    });

    controls.finalFixingDate = new DatepickerControl({
        name: ProductFieldNames.FinalFixingDate,
        value: null,
        placeholder: 'Final Fixing Date',
        validators: [],
        componentType: ComponentTypesEnum.Datepicker,
        resetable: true
    });

    controls.lastExchangeTradingDate = new DatepickerControl({
        name: ProductFieldNames.LastExchangeTradingDate,
        value: null,
        placeholder: 'Last Exchange Trading Date',
        validators: [],
        componentType: ComponentTypesEnum.Datepicker,
        resetable: true
    });

    controls.redemption = new DatepickerControl({
        name: ProductFieldNames.Redemption,
        value: null,
        placeholder: 'Redemption',
        validators: [],
        componentType: ComponentTypesEnum.Datepicker,
        resetable: true
    });

    controls.conditionalCoupon = new CheckboxControl({
        name: ProductFieldNames.ConditionalCoupon,
        value: '',
        placeholder: 'Conditional Coupon',
        validators: [],
        componentType: ComponentTypesEnum.Checkbox,
        checked: null,
        color: ColorCodesEnum.Primary,
        expectBooleanValue: true,
    });

    controls.memoryEffect = new CheckboxControl({
        name: ProductFieldNames.MemoryEffect,
        value: '',
        placeholder: 'Memory Effect',
        validators: [],
        componentType: ComponentTypesEnum.Checkbox,
        checked: null,
        color: ColorCodesEnum.Primary,
        expectBooleanValue: true,
    });

    controls.annualInterestRate = new AmountControl({
        name: ProductFieldNames.AnnualInterestRate,
        value: null,
        placeholder: 'Annual Interest Rate (%)',
        validators: [],
        componentType: ComponentTypesEnum.AmountInput,
        currency: '',
        isPercentage: true,
        fourDecimals: true
    });

    controls.interestPaymentPeriod = new SelectControl({
        name: ProductFieldNames.InterestPaymentPeriod,
        value: null,
        placeholder: 'Coupon Frequency',
        validators: [],
        componentType: ComponentTypesEnum.Select,
        options: [
            {
                value: null,
                label: 'None',
            }
        ]
    });

    Helper.getEnumNumberValues(CouponFrequency)
        .filter(key => key !== 0)
        .forEach(key => controls.interestPaymentPeriod.options.push({
            value: key,
            label: Helper.textsHelper().getInterestPaymentPeriodName(key)
        }));

    controls.couponTriggerLevel = new AmountControl({
        name: ProductFieldNames.CouponTriggerLevel,
        value: null,
        placeholder: 'Coupon Trigger Level',
        validators: [positiveDecimalValidator],
        componentType: ComponentTypesEnum.AmountInput,
        currency: '',
        isPercentage: true
    });

    controls.participation = new AmountControl({
        name: ProductFieldNames.Participation,
        value: null,
        placeholder: 'Participation',
        validators: [],
        componentType: ComponentTypesEnum.AmountInput,
        currency: '',
        isPercentage: true
    });

    controls.capLevel = new AmountControl({
        name: ProductFieldNames.CapLevel,
        value: null,
        placeholder: 'Cap Level',
        validators: [],
        componentType: ComponentTypesEnum.AmountInput,
        currency: '',
        isPercentage: true
    });

    controls.short = new CheckboxControl({
        name: ProductFieldNames.Short,
        value: '',
        placeholder: 'Short',
        validators: [],
        componentType: ComponentTypesEnum.Checkbox,
        checked: null,
        color: ColorCodesEnum.Primary,
        expectBooleanValue: true,
    });

    controls.capitalProtection = new AmountControl({
        name: ProductFieldNames.CapitalProtection,
        value: null,
        placeholder: 'Capital Protection (%)',
        validators: isCertificateOrBond ? [positiveDecimalValidator, Validators.max(100)] : [],
        componentType: ComponentTypesEnum.AmountInput,
        currency: '',
        isPercentage: true
    });

    controls.barrier = new AmountControl({
        name: ProductFieldNames.Barrier,
        value: null,
        placeholder: 'Barrier (%)',
        validators: [],
        componentType: ComponentTypesEnum.AmountInput,
        currency: '',
        isPercentage: true
    });

    controls.barrierType = new SelectControl({
        name: ProductFieldNames.BarrierType,
        value: null,
        placeholder: 'Barrier Type',
        validators: [],
        componentType: ComponentTypesEnum.Select,
        options: []
    });

    Helper.getEnumNumberValues(BarrierType)
        .forEach(key => controls.barrierType.options.push({
            value: key,
            label: Helper.textsHelper().getProductBarrierTypeName(key)
        }));

    controls.worstPerformer = new CheckboxControl({
        name: ProductFieldNames.WorstPerformer,
        value: '',
        placeholder: 'Worst Performer',
        validators: [],
        componentType: ComponentTypesEnum.Checkbox,
        checked: null,
        color: ColorCodesEnum.Primary,
        expectBooleanValue: true,
    });

    controls.strikeLevel = new AmountControl({
        name: ProductFieldNames.StrikeLevel,
        value: null,
        placeholder: 'Strike Level',
        validators: [],
        componentType: ComponentTypesEnum.AmountInput,
        currency: '',
        isPercentage: true
    });

    controls.bonusLevel = new AmountControl({
        name: ProductFieldNames.BonusLevel,
        value: null,
        placeholder: 'Bonus Level',
        validators: [],
        componentType: ComponentTypesEnum.AmountInput,
        currency: '',
        isPercentage: true
    });

    controls.bonusAmount = new AmountControl({
        name: ProductFieldNames.BonusAmount,
        value: null,
        placeholder: 'Bonus Amount',
        validators: [],
        componentType: ComponentTypesEnum.AmountInput,
        currency: ''
    });

    controls.warrantType = new SelectControl({
        name: ProductFieldNames.WarrantType,
        value: null,
        placeholder: 'Warrant Type',
        validators: [],
        componentType: ComponentTypesEnum.Select,
        options: []
    });

    Helper.getEnumNumberValues(WarrantType)
        .forEach(key => controls.warrantType.options.push({
            value: key,
            label: Helper.textsHelper().getProductWarrantTypeName(key)
        }));

    controls.conversionRatio = new AmountControl({
        name: ProductFieldNames.ConversionRatio,
        value: null,
        placeholder: 'Conversion Ratio',
        validators: [],
        componentType: ComponentTypesEnum.AmountInput,
        currency: '',
        fourDecimals: true
    });

    controls.initialLeverage = new AmountControl({
        name: ProductFieldNames.InitialLeverage,
        value: null,
        placeholder: 'Initial Leverage',
        validators: [],
        componentType: ComponentTypesEnum.AmountInput,
        currency: ''
    });

    controls.minimumBuyOrderSize = new AmountControl({
        name: ProductFieldNames.MinimumBuyOrderSize,
        value: null,
        placeholder: 'Minimum BUY Order Size',
        validators: [],
        componentType: ComponentTypesEnum.AmountInput,
        currency: ''
    });

    controls.maximumBuyOrderSize = new AmountControl({
        name: ProductFieldNames.MaximumBuyOrderSize,
        value: null,
        placeholder: 'Maximum BUY Order Size',
        validators: [],
        componentType: ComponentTypesEnum.AmountInput,
        currency: ''
    });

    controls.withdrawalBeforeMaturityFeeMin = new AmountControl({
        name: ProductFieldNames.WithdrawalBeforeMaturityFeeMin,
        value: null,
        placeholder: 'Withdrawal Before Maturity Fee (Minimum)',
        validators: [],
        componentType: ComponentTypesEnum.AmountInput,
        currency: '',
        isPercentage: true
    });

    controls.withdrawalBeforeMaturityFeeMax = new AmountControl({
        name: ProductFieldNames.WithdrawalBeforeMaturityFeeMax,
        value: null,
        placeholder: 'Withdrawal Before Maturity Fee (Maximum)',
        validators: [],
        componentType: ComponentTypesEnum.AmountInput,
        currency: '',
        isPercentage: true
    });

    Object.keys(controls).forEach(key => {
        const control = controls[key];
        control.floatingLabelNever = true;
        control.placeholder = control.placeholder.replace(/ \*/g, '');
    });

    return controls;
};

export const UnderlyingInstrumentFormControls = (): IUpdateUnderlyingInstrumentControls => {
    return {
        name: new BaseControl({
            name: ProductInstrumentFieldNames.Name,
            value: null,
            placeholder: 'Name',
            validators: [Validators.required],
            componentType: ComponentTypesEnum.Text,
            floatingLabelNever: false,
        }),
        symbol: new AutocompleteControl({
            name: ProductInstrumentFieldNames.Symbol,
            value: null,
            placeholder: 'Symbol',
            validators: [],
            componentType: ComponentTypesEnum.Autocomplete,
            floatingLabelNever: false,
            options: [],
            async: true
        }),
        currency: new SelectControl({
            name: ProductInstrumentFieldNames.Currency,
            value: null,
            placeholder: 'Currency',
            validators: [],
            componentType: ComponentTypesEnum.Select,
            floatingLabelNever: false,
            options: [
                {
                    label: 'N/A',
                    value: null,
                },
                ...Helper.getCurrencyOptions()],
        }),
        initialFixingLevel: new AmountControl({
            name: ProductInstrumentFieldNames.InitialFixingLevel,
            value: null,
            placeholder: 'Initial Fixing Level (%)',
            validators: [],
            componentType: ComponentTypesEnum.AmountInput,
            floatingLabelNever: false,
            isPercentage: true,
            currency: '',
        }),
        price: new AmountControl({
            name: ProductInstrumentFieldNames.Price,
            value: null,
            placeholder: 'Initial Fixing Level',
            validators: [],
            componentType: ComponentTypesEnum.AmountInput,
            floatingLabelNever: false,
            currency: '',
            fourDecimals: true
        }),
        weight: new AmountControl({
            name: ProductInstrumentFieldNames.Weight,
            value: null,
            placeholder: 'Weight',
            validators: [],
            componentType: ComponentTypesEnum.AmountInput,
            floatingLabelNever: false,
            currency: ''
        }),
    };
};

export const EarlyRedemptionFormControls = (): IUpdateEarlyRedemptionControls => {
    const minValidator = Validators.min(0.01);
    const maxValidator = Validators.max(100);

    return {
        amount: new AmountControl({
            name: ProductRedemptionFieldNames.Amount,
            value: null,
            placeholder: 'Amount',
            validators: [Validators.required, minValidator, maxValidator],
            componentType: ComponentTypesEnum.AmountInput,
            floatingLabelNever: false,
            currency: '',
            isPercentage: true
        }),
        triggerLevel: new AmountControl({
            name: ProductRedemptionFieldNames.TriggerLevel,
            value: null,
            placeholder: 'Trigger Level',
            validators: [Validators.required, minValidator],
            componentType: ComponentTypesEnum.AmountInput,
            floatingLabelNever: false,
            currency: '',
            isPercentage: true
        }),
        observationDate: new DatepickerControl({
            name: ProductRedemptionFieldNames.ObservationDate,
            value: null,
            placeholder: 'Observation Date',
            validators: [Validators.required],
            componentType: ComponentTypesEnum.Datepicker,
            floatingLabelNever: true,
        }),
        date: new DatepickerControl({
            name: ProductRedemptionFieldNames.Date,
            value: null,
            placeholder: 'Redemption Date',
            validators: [Validators.required],
            componentType: ComponentTypesEnum.Datepicker,
            floatingLabelNever: true,
        }),
    };
};

export const CouponPaymentFormControls = (): IUpdateCouponPaymentControls => {
    return {
        amount: new AmountControl({
            name: ProductCouponFieldNames.Amount,
            value: null,
            placeholder: 'Amount',
            validators: [Validators.required],
            componentType: ComponentTypesEnum.AmountInput,
            floatingLabelNever: false,
            currency: ''
        }),
        observationDate: new DatepickerControl({
            name: ProductCouponFieldNames.ObservationDate,
            value: null,
            placeholder: 'Observation Date',
            validators: [],
            componentType: ComponentTypesEnum.Datepicker,
            floatingLabelNever: true,
        }),
        paymentDate: new DatepickerControl({
            name: ProductCouponFieldNames.PaymentDate,
            value: null,
            placeholder: 'Payment / Ex Date',
            validators: [],
            componentType: ComponentTypesEnum.Datepicker,
            floatingLabelNever: true,
        }),
    };
};

export const CreateProductDialogConfig = (): DialogConfigInterface => {
    return {
        process: {
            loading: false,
            finished: false,
            failed: false,
            failedMessage: '',
            succeedMessage: ''
        },
        buttons: [
            {
                type: DialogButtonTypesEnum.Cancel
            },
            {
                type: DialogButtonTypesEnum.Create,
            }
        ],
        buttonsDisabled: false,
        buttonsHidden: false,
        disabledCloseBtn: true
    };
};
